import { assignmentFolders } from "./assignmentFolders.js";
import { studentNumberLabel } from "./roster.js";
import type { Classroom, HomeroomRecord, HomeworkAssignment, Student } from "./types.js";

type Affair = HomeroomRecord["affairs"][number];
export type HomeroomAffairChange = { before?: Affair; after?: Affair };

export function applyHomeroomAffairChange(record: HomeroomRecord, change: HomeroomAffairChange): HomeroomRecord {
  const { before, after } = change;
  const id = before?.id || after?.id;
  if (
    !id ||
    (before && after && before.id !== after.id) ||
    (after && (!after.title.trim() || after.title.length > 200 || (after.note?.length || 0) > 10000))
  )
    throw new Error("invalid-affair");
  const current = record.affairs.find((item) => item.id === id);
  const same = (a?: Affair, b?: Affair) =>
    a?.id === b?.id && a?.title === b?.title && (a?.note || "") === (b?.note || "");
  // A reply can be lost after commit. Repeating the same explicit change is safe.
  if (same(current, after)) return record;
  if (!same(current, before)) throw new Error("stale-affair");
  return {
    ...record,
    affairs: after
      ? current
        ? record.affairs.map((item) => (item.id === id ? { ...after, title: after.title.trim() } : item))
        : [...record.affairs, { ...after, title: after.title.trim() }]
      : record.affairs.filter((item) => item.id !== id),
  };
}

export type AssignmentCopyTarget = { classId: string; id: string; dueDate: string; folderId: string };
export type AssignmentCopyRequest = { source: HomeworkAssignment; targets: AssignmentCopyTarget[]; createdAt: string };

export function assignmentCopies(
  source: HomeworkAssignment,
  sourceClass: Classroom,
  classes: Classroom[],
  targets: AssignmentCopyTarget[],
  now: string,
): HomeworkAssignment[] {
  const ids = new Set<string>(),
    classIds = new Set<string>();
  if (
    source.classId !== sourceClass.id ||
    source.workspaceId !== sourceClass.workspaceId ||
    sourceClass.classRole !== "teaching" ||
    !sourceClass.active ||
    !targets.length
  )
    throw new Error("invalid-copy");
  return targets.map((target) => {
    const classroom = classes.find((item) => item.id === target.classId);
    if (
      !classroom ||
      !classroom.active ||
      classroom.classRole !== "teaching" ||
      classroom.id === sourceClass.id ||
      classroom.workspaceId !== source.workspaceId ||
      classroom.schoolYearId !== sourceClass.schoolYearId ||
      !target.id?.trim() ||
      target.id === source.id ||
      ids.has(target.id) ||
      classIds.has(target.classId) ||
      !/^\d{4}-\d{2}-\d{2}$/.test(target.dueDate) ||
      !Number.isFinite(Date.parse(target.dueDate)) ||
      new Date(target.dueDate).toISOString().slice(0, 10) !== target.dueDate ||
      (target.folderId !== "uncategorized" &&
        !assignmentFolders(classroom).some((folder) => folder.id === target.folderId))
    )
      throw new Error("invalid-copy-target");
    ids.add(target.id);
    classIds.add(target.classId);
    return {
      id: target.id,
      workspaceId: source.workspaceId,
      classId: target.classId,
      title: source.title,
      dueDate: target.dueDate,
      category: source.category,
      folderId: target.folderId,
      note: source.note,
      maxScore: source.maxScore,
      createdAt: now,
      updatedAt: now,
    };
  });
}

export type PastedScore = {
  line: number;
  label: string;
  studentId?: string;
  score?: number;
  error?: "rowCount" | "columns" | "student" | "duplicate" | "score";
};
export function parseAssignmentScores(
  text: string,
  students: Student[],
  mode: "order" | "number",
  maxScore?: number,
): PastedScore[] {
  if (!text.trim()) return [];
  const lines = text.replace(/\r\n?/g, "\n").replace(/\n$/, "").split("\n");
  if (mode === "order" && lines.length !== students.length) return [{ line: 1, label: "", error: "rowCount" }];
  const normalize = (s: string) => s.normalize("NFKC").replace(/\s/g, "").toUpperCase();
  const seen = new Set<string>();
  return lines.map((line, index) => {
    const fields = line.split("\t");
    const result: PastedScore = { line: index + 1, label: fields[0] || "" };
    if (fields.length !== (mode === "order" ? 1 : 2)) return { ...result, error: "columns" };
    const matches =
      mode === "order"
        ? [students[index]]
        : students.filter(
            (student) =>
              normalize(studentNumberLabel(student)) === normalize(fields[0] || "") ||
              String(student.number) === normalize(fields[0] || ""),
          );
    if (matches.length !== 1) return { ...result, error: "student" };
    const student = matches[0]!;
    result.studentId = student.id;
    result.label = `${studentNumberLabel(student)} ${student.name}`;
    if (seen.has(student.id)) return { ...result, error: "duplicate" };
    seen.add(student.id);
    const raw = fields.at(-1)!.normalize("NFKC").trim();
    if (!raw) return result;
    const score = Number(raw);
    if (
      !/^(?:\d+(?:\.\d*)?|\.\d+)$/.test(raw) ||
      !Number.isFinite(score) ||
      score < 0 ||
      (maxScore !== undefined && score > maxScore)
    )
      return { ...result, error: "score" };
    return { ...result, score };
  });
}
